import { default as React } from 'react';
import {
  alertApiRef,
  configApiRef,
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  OndemandAdminClient,
  OndemandAdminWorkload,
} from '@internal/ondemand-admin-api-helper';
import { WorkloadClient } from '@internal/workload-api-helper';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DialogActions,
  DialogContent,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  makeStyles,
  CircularProgress,
} from '@material-ui/core';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useAsync from 'react-use/lib/useAsync';
import { SeverityEnum } from '../../../enums/SeverityEnum';
import { getMessage } from '../../../utils/getMessage';
import {
  Account,
  Annotations,
  OnDemandFormFieldName,
  OndemandAdminCardProps,
} from '../types';
import { isResponseCodeAcceptable, isValueDefined } from '../utils/checks';
import { OndemandAdminInput } from './OndemandAdminInput';
import { OndemandAdminRequestIcon } from './OndemandAdminRequestIcon';
import { intervalToDuration } from 'date-fns';

let accountId: string;
let workloadName: string;

const useStyles = makeStyles({
  gridItemCard: {
    display: 'flex',
    flexDirection: 'column',
  },
  fullHeightCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  gridItemCardContent: {
    flex: 1,
  },
  fullHeightCardContent: {
    flex: 1,
  },
});

const fetchWorkloadData = async (workloadClient: WorkloadClient) => {
  return await workloadClient.apiFetch(`workloads/${accountId}`);
};

const fetchOndemandAdminData = async (
  ondemandAdminClient: OndemandAdminClient,
) => {
  return await ondemandAdminClient.apiFetch(`${workloadName}/${accountId}`);
};

const parseWorkloadObjectValues = (
  catalogLocationStringList: string[] | undefined,
): { [key: string]: string | undefined } => {
  const resourceFileName: string | undefined = catalogLocationStringList?.pop();
  const resourceName = resourceFileName?.split('.')[0];
  const workload = catalogLocationStringList?.pop();

  return {
    resourceName,
    workload,
  };
};

export function OndemandAdminCard(props: OndemandAdminCardProps) {
  const [requesterEmail, setRequesterEmail] = React.useState('');
  const [requesterName, setRequesterName] = React.useState('');
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const alertApi = useApi(alertApiRef);
  const identity = useApi(identityApiRef);
  const config = useApi(configApiRef);
  const [workloadData, setWorkloadData] = useState<Account>();
  const [ondemandAdminData, setOndemandAdminData] =
    useState<OndemandAdminWorkload>();
  const [workloadDataAPIResponse, setWorkloadDataAPIResponse] =
    useState<Response>();

  const configApi = useApi(configApiRef);
  const oauthApi = useApi(microsoftAuthApiRef);
  const workloadClient = new WorkloadClient(configApi, oauthApi);
  const ondemandAdminClient = new OndemandAdminClient(configApi, oauthApi);
  useAsync(async (): Promise<OndemandAdminWorkload> => {
    const [workloadDetails] = await Promise.all([
      fetchWorkloadData(workloadClient),
    ]);

    setWorkloadDataAPIResponse(workloadDetails);

    const [details] = await Promise.all([workloadDetails.json()]);
    const { email } = await identity.getProfileInfo();
    if (email) {
      setRequesterEmail(email);
    }

    setWorkloadData(details);
    workloadName = details?.workloadName || '';

    const [ondemandAdminDetails] = await Promise.all([
      fetchOndemandAdminData(ondemandAdminClient),
    ]);

    const [ondemandAdminDetail] = await Promise.all([
      ondemandAdminDetails.json(),
    ]);
    setOndemandAdminData(ondemandAdminDetail);

    return ondemandAdminDetail;
  }, []);

  useEffect(() => {
    async function getRequesterEmail() {
      const profile = await identity.getProfileInfo();
      setRequesterName(profile.displayName || '');
      setRequesterEmail(profile.email || '');
    }
    getRequesterEmail();
  }, [identity]);

  const { entity } = useEntity();
  const annotations = entity?.metadata?.annotations as Annotations;
  accountId = annotations?.['amazonaws.com/account-id'];

  const FormFieldEnum: OnDemandFormFieldName = Object.freeze({
    justification: 'justification',
  });

  const formFields = [...Object.values(FormFieldEnum)];

  const formContext = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    defaultValues: {
      [FormFieldEnum.justification]: '',
    },
  });

  const apiValidationPayload = {
    form: formContext,
    identity: identity,
    config: config,
  };

  const { variant } = props;
  const classes = useStyles();
  let cardClass = '';
  let cardContentClass = '';
  if (variant === 'gridItem') {
    cardClass = classes.gridItemCard;
    cardContentClass = classes.gridItemCardContent;
  } else if (variant === 'fullHeight') {
    cardClass = classes.fullHeightCard;
    cardContentClass = classes.fullHeightCardContent;
  }

  useEffect(() => {
    async function getRequesterEmail() {
      const profile = await identity.getProfileInfo();
      setRequesterEmail(profile.email || '');
    }
    getRequesterEmail();
  }, [identity]);

  const handleClickOpen = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const stopLoadingAndCloseModal = () => {
    setIsModalOpen(false);
    setIsLoading(false);
  };

  const isFormValid = async () => {
    let response = false;

    await Promise.all(
      formFields.map(fieldName => formContext.trigger(fieldName)),
    );

    if (Object.keys(formContext.formState.errors).length === 0) {
      response = true;
    }

    return response;
  };

  const handleConfirm = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);

    if (!(await isFormValid())) {
      return Promise.resolve();
    }

    const formValues: { [key: string]: string } =
      apiValidationPayload.form.getValues();
    const payload = formValues[FormFieldEnum.justification];
    const justification = payload;

    let workloadObject: { [key: string]: string | undefined } = {};
    let workloadDataStatus: number | undefined;

    const stripCatalogLocation = workloadData?.catalogLocation;
    const catalogLocationStringList = stripCatalogLocation?.split('/');
    if (workloadDataAPIResponse?.status && catalogLocationStringList) {
      workloadDataStatus = workloadDataAPIResponse?.status;
      workloadObject = parseWorkloadObjectValues(catalogLocationStringList);
    } else {
      stopLoadingAndCloseModal();
      alertApi.post({
        message: getMessage({
          failureToDo: 'find your resource with the data provided',
        }).FAILURE,
        severity: SeverityEnum.ERROR,
      });
      return Promise.resolve();
    }

    if (
      isResponseCodeAcceptable(workloadDataStatus) &&
      isValueDefined(workloadObject?.workload)
    ) {
      await ondemandAdminClient.requestOndemandAdmin({
        accountId,
        requesterEmail,
        workloadName,
        justification,
      });

      stopLoadingAndCloseModal();
      alertApi.post({
        message: 'Request submitted successfully',
        severity: SeverityEnum.SUCCESS,
      });
    } else {
      setIsLoading(false);
    }
    return Promise.resolve();
  };

  let currentSession: string = 'Loading...';
  let sessionAvailable: boolean = false;

  if (
    !ondemandAdminData?.ondemandAdminMembership?.some(
      member => member?.toLowerCase() === requesterEmail.toLowerCase(),
    )
  ) {
    currentSession = 'N/A';
  } else {
    if (ondemandAdminData?.currentUserSessionEndTime === null) {
      currentSession = 'No active session found';
      sessionAvailable = true;
    }
    if (ondemandAdminData?.currentUserSessionEndTime !== undefined) {
      const now = new Date();
      const endDate = new Date(
        ondemandAdminData?.currentUserSessionEndTime || now,
      );

      if (endDate <= now) {
        sessionAvailable = true;
        currentSession = 'No active session found';
      } else {
        const duration = intervalToDuration({
          start: now,
          end: endDate,
        });

        currentSession = `Ending: ${endDate.toLocaleString()} (${
          duration.hours || 0
        } hours ${duration.minutes} minutes remaining)`;
      }
    }
  }

  return (
    <Card className={cardClass}>
      <CardHeader
        title="AWS On-Demand Admin Access"
        titleTypographyProps={{ component: 'h2' }}
        action={
          <OndemandAdminRequestIcon
            apiValidationPayload={apiValidationPayload}
            cardClass={cardClass}
            cardContentClass={cardContentClass}
            handleClickOpen={handleClickOpen}
            handleClose={handleClose}
            handleConfirm={handleConfirm}
            isLoading={isLoading}
            isModalOpen={isModalOpen}
            requesterEmail={requesterEmail}
            workloadData={workloadData}
            ondemandAdminData={ondemandAdminData}
          />
        }
      />

      <Divider />
      <CardContent className={cardContentClass}>
        <form onSubmit={handleConfirm} autoComplete="off">
          <DialogContent>
            <FormProvider {...apiValidationPayload.form}>
              <TableContainer component={Paper}>
                <Table aria-label="breakfix information">
                  <TableBody>
                    <TableRow>
                      <TableCell align="left" style={{ fontWeight: 'bold' }}>
                        Workload:
                      </TableCell>
                      <TableCell>{workloadData?.workloadName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="left" style={{ fontWeight: 'bold' }}>
                        Account ID:
                      </TableCell>
                      <TableCell>{accountId}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="left" style={{ fontWeight: 'bold' }}>
                        Workload Name:
                      </TableCell>
                      <TableCell>{workloadName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="left" style={{ fontWeight: 'bold' }}>
                        Requester:
                      </TableCell>
                      <TableCell>{requesterName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="left" style={{ fontWeight: 'bold' }}>
                        Current Session:
                      </TableCell>
                      <TableCell>
                        {ondemandAdminData?.ondemandAdminMembership
                          ? currentSession
                          : 'Loading....'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="left" style={{ fontWeight: 'bold' }}>
                        Entitlement Membership:
                      </TableCell>
                      <TableCell
                        style={{
                          whiteSpace: 'pre-wrap',
                          wordWrap: 'break-word',
                        }}
                      >
                        {ondemandAdminData?.ondemandAdminMembership
                          ? ondemandAdminData?.ondemandAdminMembership?.join(
                              '\n',
                            )
                          : 'Loading...'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <br />

              <OndemandAdminInput disabled={!sessionAvailable} />
            </FormProvider>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={isLoading || !sessionAvailable}
              type="submit"
              color="primary"
            >
              Submit Request
            </Button>
            {isLoading && <CircularProgress size={24} />}
          </DialogActions>
        </form>
      </CardContent>
    </Card>
  );
}
